import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Filter, Search } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

import ExerciseCard from "@/components/admin/exercises/ExerciseCard";
import { getExerciseCardImageSrc, getVideoEmbedUrl } from "@/lib/exercise-media";
import GroupedMuscleActionFilterItems from "@/components/admin/exercises/GroupedMuscleActionFilterItems";
import { EXERCISE_DIFFICULTY_OPTIONS, matchesExerciseLibraryFilters } from "@/lib/exercise-library-filters";
import { exerciseTypeCatalog, exerciseTypeLabelById } from "@/lib/exercise-types";
import { listExercises, type ClientExerciseVM } from "@/integrations/backend/exercises";

type Props = {
  onBack: () => void;
  initialSearch?: string;
};

function uniqSorted(items: string[]): string[] {
  return Array.from(new Set(items)).sort((a, b) => a.localeCompare(b));
}

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}

function filterButtonClass(isActive: boolean): string {
  return isActive
    ? "border-primary/30 bg-primary/10 text-white hover:bg-primary/15"
    : "border-primary/10 bg-dark-card text-white hover:bg-dark-slate";
}

export default function ClientExercises({ onBack, initialSearch }: Props) {
  const [loading, setLoading] = useState(false);
  const [exercises, setExercises] = useState<ClientExerciseVM[]>([]);

  const [query, setQuery] = useState((initialSearch ?? "").trim());
  const [movementFilter, setMovementFilter] = useState<string[]>([]);
  const [muscleFilter, setMuscleFilter] = useState<string[]>([]);
  const [equipmentFilter, setEquipmentFilter] = useState<string[]>([]);
  const [difficultyFilter, setDifficultyFilter] = useState<string[]>([]);
  const [muscleActionFilter, setMuscleActionFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);

  const [selectedExercise, setSelectedExercise] = useState<ClientExerciseVM | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const rows = await listExercises();
        if (!active) return;
        setExercises(rows);
      } catch (e: unknown) {
        toast.error("Could not load exercises", { description: errorMessage(e) });
        setExercises([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const movementOptions = useMemo(() => uniqSorted(exercises.flatMap((e) => e.categories || []).map(String)), [exercises]);
  const muscleOptions = useMemo(
    () =>
      uniqSorted(
        exercises.flatMap((e) => [...(e.primary_muscles || []), ...(e.secondary_muscles || [])]).map(String)
      ),
    [exercises]
  );
  const equipmentOptions = useMemo(() => uniqSorted(exercises.flatMap((e) => e.equipment || [])), [exercises]);

  const availableTypeIds = useMemo(() => uniqSorted(exercises.flatMap((e) => (e.exercise_types || []).map(String))), [exercises]);
  const typeOptions = useMemo(() => {
    if (availableTypeIds.length === 0) return [];
    return exerciseTypeCatalog
      .filter((t) => availableTypeIds.includes(t.id))
      .map((t) => ({ id: t.id, label: t.label }));
  }, [availableTypeIds]);

  const filtered = useMemo(() => {
    return exercises.filter((e) =>
      matchesExerciseLibraryFilters(e, {
        searchQuery: query,
        movementPatterns: movementFilter,
        muscleGroups: muscleFilter,
        equipment: equipmentFilter,
        muscleActions: muscleActionFilter,
        difficulties: difficultyFilter,
        exerciseTypes: typeFilter,
      })
    );
  }, [difficultyFilter, equipmentFilter, exercises, muscleActionFilter, muscleFilter, movementFilter, query, typeFilter]);

  const clearAll = () => {
    setQuery("");
    setMovementFilter([]);
    setMuscleFilter([]);
    setEquipmentFilter([]);
    setDifficultyFilter([]);
    setMuscleActionFilter([]);
    setTypeFilter([]);
  };

  const activeFilterCount =
    movementFilter.length +
    muscleFilter.length +
    equipmentFilter.length +
    difficultyFilter.length +
    muscleActionFilter.length +
    typeFilter.length;

  return (
    <div className="min-h-screen text-white">
      <div className="p-6 pt-8 pb-6 md:ml-16">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" className="text-white hover:bg-dark-slate" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Exercises</h1>
        </div>

        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-light w-5 h-5" />
            <Input
              placeholder="Search by name, muscles, equipment..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 bg-dark-card border border-primary/10 text-white placeholder:text-gray-light"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(movementFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Movement
                  {movementFilter.length > 0 ? <span className="ml-2 text-xs">({movementFilter.length})</span> : null}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>Movement pattern</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {movementOptions.map((opt) => (
                  <DropdownMenuCheckboxItem
                    key={opt}
                    checked={movementFilter.includes(opt)}
                    onCheckedChange={(checked) => {
                      setMovementFilter((prev) => (checked ? [...prev, opt] : prev.filter((x) => x !== opt)));
                    }}
                  >
                    {opt}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(muscleFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Muscles
                  {muscleFilter.length > 0 ? <span className="ml-2 text-xs">({muscleFilter.length})</span> : null}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 max-h-[60vh] overflow-auto">
                <DropdownMenuLabel>Muscle groups</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {muscleOptions.map((opt) => (
                  <DropdownMenuCheckboxItem
                    key={opt}
                    checked={muscleFilter.includes(opt)}
                    onCheckedChange={(checked) => {
                      setMuscleFilter((prev) => (checked ? [...prev, opt] : prev.filter((x) => x !== opt)));
                    }}
                  >
                    {opt}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(equipmentFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Equipment
                  {equipmentFilter.length > 0 ? <span className="ml-2 text-xs">({equipmentFilter.length})</span> : null}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 max-h-[60vh] overflow-auto">
                <DropdownMenuLabel>Equipment</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {equipmentOptions.map((opt) => (
                  <DropdownMenuCheckboxItem
                    key={opt}
                    checked={equipmentFilter.includes(opt)}
                    onCheckedChange={(checked) => {
                      setEquipmentFilter((prev) => (checked ? [...prev, opt] : prev.filter((x) => x !== opt)));
                    }}
                  >
                    {opt}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(difficultyFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Difficulty
                  {difficultyFilter.length > 0 ? <span className="ml-2 text-xs">({difficultyFilter.length})</span> : null}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>Difficulty</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {EXERCISE_DIFFICULTY_OPTIONS.map((opt) => (
                  <DropdownMenuCheckboxItem
                    key={opt}
                    checked={difficultyFilter.includes(opt)}
                    onCheckedChange={(checked) => {
                      setDifficultyFilter((prev) => (checked ? [...prev, opt] : prev.filter((x) => x !== opt)));
                    }}
                  >
                    {opt}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(muscleActionFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Actions
                  {muscleActionFilter.length > 0 ? <span className="ml-2 text-xs">({muscleActionFilter.length})</span> : null}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[min(100vw-2rem,28rem)] max-h-[60vh] overflow-auto">
                <DropdownMenuLabel>Muscle actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <GroupedMuscleActionFilterItems
                  selected={muscleActionFilter}
                  onToggle={(action, checked) => {
                    setMuscleActionFilter((prev) =>
                      checked ? [...prev, action] : prev.filter((x) => x !== action)
                    );
                  }}
                  onClear={() => setMuscleActionFilter([])}
                  checkboxClassName=""
                />
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={filterButtonClass(typeFilter.length > 0)}>
                  <Filter className="w-4 h-4 mr-2" /> Types
                  {typeFilter.length > 0 ? <span className="ml-2 text-xs">({typeFilter.length})</span> : null}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 max-h-[60vh] overflow-auto">
                <DropdownMenuLabel>Exercise types</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {typeOptions.map((opt) => (
                  <DropdownMenuCheckboxItem
                    key={opt.id}
                    checked={typeFilter.includes(opt.id)}
                    onCheckedChange={(checked) => {
                      setTypeFilter((prev) => (checked ? [...prev, opt.id] : prev.filter((x) => x !== opt.id)));
                    }}
                  >
                    {opt.label}
                  </DropdownMenuCheckboxItem>
                ))}
                {typeOptions.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">No exercise types available</div>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex-1" />

            <Button
              variant="outline"
              className={filterButtonClass(activeFilterCount > 0 || query.trim().length > 0)}
              onClick={clearAll}
              disabled={activeFilterCount === 0 && query.trim().length === 0}
            >
              Clear
            </Button>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-light">
            <div>
              {loading ? "Loading…" : `${filtered.length} exercise${filtered.length !== 1 ? "s" : ""}`}
              {activeFilterCount > 0 ? <span className="ml-2">({activeFilterCount} filters)</span> : null}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 pb-24 md:ml-16">
        {loading ? (
          <div className="text-gray-light">Loading exercises…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-dark-card border border-primary/10 rounded-2xl p-6">
            <div className="text-white font-semibold mb-1">No exercises found</div>
            <div className="text-gray-light text-sm mb-4">Try clearing filters or searching a different keyword.</div>
            <Button variant="outline" className={filterButtonClass(true)} onClick={clearAll}>
              Clear all
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((ex) => (
              <ExerciseCard
                key={ex.id}
                exercise={ex}
                onClick={() => setSelectedExercise(ex)}
                variant="dark"
                rightBadge={<Badge variant="outline" className="bg-black/40 text-white border-white/15 text-xs">View</Badge>}
              />
            ))}
          </div>
        )}
      </div>

      <Dialog open={Boolean(selectedExercise)} onOpenChange={(open) => !open && setSelectedExercise(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-3xl bg-black text-white border border-primary/10 p-0 overflow-hidden">
          {selectedExercise ? (
            <>
              <div className="p-6 pb-4 border-b border-primary/10">
                <DialogHeader>
                  <DialogTitle className="text-white">{selectedExercise.name}</DialogTitle>
                  <DialogDescription className="text-gray-light">
                    Instructions, media, and exercise details
                  </DialogDescription>
                </DialogHeader>
              </div>

              <ScrollArea className="max-h-[calc(100vh-12rem)]">
                <div className="p-6 space-y-4">
                  <div className="rounded-xl overflow-hidden border border-primary/10 bg-black">
                    {selectedExercise.video_url && getVideoEmbedUrl(selectedExercise.video_url) ? (
                      <div className="aspect-video w-full">
                        <iframe
                          src={getVideoEmbedUrl(selectedExercise.video_url) as string}
                          title={`${selectedExercise.name} video`}
                          className="h-full w-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="aspect-video w-full">
                        <img
                          src={getExerciseCardImageSrc({
                            name: selectedExercise.name,
                            image_url: selectedExercise.image_url,
                            video_url: selectedExercise.video_url,
                          })}
                          alt={`${selectedExercise.name} demonstration`}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {(selectedExercise.categories || []).map((c) => (
                      <Badge key={c} variant="secondary" className="bg-dark-slate text-white border border-primary/10">
                        {String(c)}
                      </Badge>
                    ))}
                    {selectedExercise.is_compound ? (
                      <Badge variant="outline" className="text-purple-200 border-purple-300/30 bg-black/30">
                        Compound
                      </Badge>
                    ) : null}
                    {(selectedExercise.exercise_types || []).map((t) => (
                      <Badge key={t} variant="outline" className="text-white border-white/15 bg-black/30">
                        {exerciseTypeLabelById[String(t)] ?? String(t)}
                      </Badge>
                    ))}
                  </div>

                  {(selectedExercise.primary_muscles?.length || 0) > 0 ? (
                    <div>
                      <div className="text-sm font-semibold mb-2">Primary muscles</div>
                      <div className="flex flex-wrap gap-2">
                        {(selectedExercise.primary_muscles || []).map((m) => (
                          <Badge key={String(m)} variant="secondary" className="bg-dark-slate text-white border border-primary/10">
                            {String(m)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {(selectedExercise.secondary_muscles?.length || 0) > 0 ? (
                    <div>
                      <div className="text-sm font-semibold mb-2">Secondary muscles</div>
                      <div className="flex flex-wrap gap-2">
                        {(selectedExercise.secondary_muscles || []).map((m) => (
                          <Badge key={String(m)} variant="outline" className="text-white border-white/15 bg-black/30">
                            {String(m)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {(selectedExercise.equipment?.length || 0) > 0 ? (
                    <div>
                      <div className="text-sm font-semibold mb-2">Equipment</div>
                      <div className="flex flex-wrap gap-2">
                        {(selectedExercise.equipment || []).map((eq) => (
                          <Badge key={eq} variant="outline" className="text-orange-200 border-orange-300/30 bg-black/30">
                            {eq}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {(selectedExercise.difficulties?.length || 0) > 0 ? (
                    <div>
                      <div className="text-sm font-semibold mb-2">Difficulty</div>
                      <div className="flex flex-wrap gap-2">
                        {(selectedExercise.difficulties || []).map((level) => (
                          <Badge key={String(level)} variant="secondary" className="bg-dark-slate text-white border border-primary/10">
                            {String(level)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {(selectedExercise.muscle_actions?.length || 0) > 0 ? (
                    <div>
                      <div className="text-sm font-semibold mb-2">Muscle actions</div>
                      <div className="flex flex-wrap gap-2">
                        {(selectedExercise.muscle_actions || []).map((action) => (
                          <Badge key={String(action)} variant="outline" className="text-white border-white/15 bg-black/30">
                            {String(action)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div>
                    <div className="text-sm font-semibold mb-2">Instructions</div>
                    <div className="text-sm text-gray-light whitespace-pre-wrap">
                      {selectedExercise.instructions || "No instructions available."}
                    </div>
                  </div>

                  {selectedExercise.tips ? (
                    <div>
                      <div className="text-sm font-semibold mb-2">Tips</div>
                      <div className="text-sm text-gray-light whitespace-pre-wrap">{selectedExercise.tips}</div>
                    </div>
                  ) : null}

                  {selectedExercise.programming_use ? (
                    <div>
                      <div className="text-sm font-semibold mb-2">Programming notes</div>
                      <div className="text-sm text-gray-light whitespace-pre-wrap">{selectedExercise.programming_use}</div>
                    </div>
                  ) : null}

                  {selectedExercise.video_url && !getVideoEmbedUrl(selectedExercise.video_url) ? (
                    <div className="flex justify-end">
                      <a
                        href={selectedExercise.video_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-medium text-primary hover:text-primary/90"
                      >
                        Open video ↗
                      </a>
                    </div>
                  ) : null}
                </div>
              </ScrollArea>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

